import React, { useState } from "react";
import { useParams } from "react-router";
import { Settings2, X } from "lucide-react";

import { Button } from "@/components/ui/button";

import { UserRole } from "../models/User";
import SourceLinksTable, { SourceLinksExportScope } from "../components/SourceLinks/SourceLinksTable";
import SourceLinkExports from "../components/SourceLinks/SourceLinkExports";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";
import { useManagementDrawer } from "../hooks/useManagementDrawer";

interface SourceLinksPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

/**
 * The map of source links: every path the firm's case-management system holds and every configured root a
 * discovery walked, with what became of each. Opens on the exceptions — the links that lead nowhere, since a
 * path that leads nowhere is a document that never arrives — and reaches the healthy links, the roots and the
 * files each link produced from there. Exports live in the Manage drawer, as on the other entity pages.
 */
const SourceLinksPage: React.FC<SourceLinksPageProps> = ({ userRole, isAssignedUser }) => {
  const { organizationId } = useParams();
  const [exportScope, setExportScope] = useState<SourceLinksExportScope | null>(null);
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(0);
  const canManage = userRole !== UserRole.Viewer && !!organizationId;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Source Links<DocsHelpLink to="concepts/source-links-and-discovery-cycles" /></h2>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => setDrawerOpen(!drawerOpen)} className="gap-2">
            <Settings2 className="size-4" />
            Manage
          </Button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          <SourceLinksTable userRole={userRole} onExportScopeChange={setExportScope} />
        </div>
        {canManage && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage source links</h3>
              <Button variant="ghost" size="icon-sm" onClick={() => setDrawerOpen(false)} aria-label="Close">
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <SourceLinkExports
                organizationId={organizationId!}
                scope={exportScope}
                userRole={userRole}
                isAssignedUser={isAssignedUser}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SourceLinksPage;
